import { useCallback, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { ExpandMore, ZoomOutMap } from '@mui/icons-material';
import type Highcharts from 'highcharts';
import type {
  TriaxialGeneral,
  TriaxialReading,
  TriaxialPicker,
} from '../../../types/triaxial';
import { updateTriaxialPicker, updateTriaxialSelectedPhi } from '../../../services/triaxialService';
import { TriaxialSummaryTable } from './TriaxialSummaryTable';
import { TriaxialComputedValuesCard } from './TriaxialComputedValuesCard';
import { SyncedChart } from '../analysis-plots/SyncedChart';
import { useSyncedCharts } from '../analysis-plots/useSyncedCharts';
import { baseChartOptions, CHART_COLORS } from '../analysis-plots/chartConfig';
import { TriaxialPickerInputs } from './TriaxialPickerInputs';
import {
  resolveSlotSnapshot,
  type SlotRange,
  type SlotSnapshot,
  EMPTY_SNAPSHOT,
} from './triaxialPickerResolver';
import {
  TRIAXIAL_SLOT_LABEL,
  TRIAXIAL_SLOT_ORDER,
  type TriaxialSlot,
} from './triaxialPickerColors';
import {
  buildSlotPlotBands,
  buildPointPickPlotLines,
  buildSlotMarkers,
  type SlotState,
} from './triaxialPickerOverlay';

interface Props {
  workOrderDetailKey: string;
  general: TriaxialGeneral;
  readings: TriaxialReading[];
  onReloadRequested: () => void;
  onSilentUpdateGeneral: (fields: Partial<TriaxialGeneral>) => void;
  onNotify: (message: string, severity: 'success' | 'error') => void;
  readOnly?: boolean;
}

// BUG-2026-0111 — pull the backend's error message out of an axios rejection
// so a failed save names the real problem instead of failing silently.
const apiErrorMessage = (err: unknown): string | null => {
  const data = (err as { response?: { data?: { error?: unknown } } })?.response?.data;
  return typeof data?.error === 'string' && data.error ? data.error : null;
};

const fmtX = (v: number) => v.toFixed(2);
const fmtKpa = (v: number) => v.toFixed(0);

// Map slot → general-row field accessors for range + snapshot
const SLOT_RANGE_KEYS: Record<TriaxialSlot, { from: keyof TriaxialGeneral; to: keyof TriaxialGeneral }> = {
  max: { from: 'maxFromPct', to: 'maxToPct' },
  peak: { from: 'peakFromPct', to: 'peakToPct' },
  end: { from: 'endFromPct', to: 'endToPct' },
  residual: { from: 'residualFromPct', to: 'residualToPct' },
};

const SLOT_SNAPSHOT_KEYS: Record<TriaxialSlot, Record<keyof SlotSnapshot, keyof TriaxialGeneral>> = {
  max: {
    axialStrainPct: 'maxAxialStrainPct',
    deviatorStressKPa: 'maxDeviatorStressKPa',
    cambridgePKPa: 'maxCambridgePKPa',
    effCambridgePKPa: 'maxEffCambridgePKPa',
    tauTKPa: 'maxTauTKPa',
    meanStressSKPa: 'maxMeanStressSKPa',
    effStressRatio: 'maxEffStressRatio',
    excessPwpKPa: 'maxExcessPwpKPa',
  },
  peak: {
    axialStrainPct: 'peakAxialStrainPct',
    deviatorStressKPa: 'peakDeviatorStressKPa',
    cambridgePKPa: 'peakCambridgePKPa',
    effCambridgePKPa: 'peakEffCambridgePKPa',
    tauTKPa: 'peakTauTKPa',
    meanStressSKPa: 'peakMeanStressSKPa',
    effStressRatio: 'peakEffStressRatio',
    excessPwpKPa: 'peakExcessPwpKPa',
  },
  end: {
    axialStrainPct: 'endAxialStrainPct',
    deviatorStressKPa: 'endDeviatorStressKPa',
    cambridgePKPa: 'endCambridgePKPa',
    effCambridgePKPa: 'endEffCambridgePKPa',
    tauTKPa: 'endTauTKPa',
    meanStressSKPa: 'endMeanStressSKPa',
    effStressRatio: 'endEffStressRatio',
    excessPwpKPa: 'endExcessPwpKPa',
  },
  residual: {
    axialStrainPct: 'residualAxialStrainPct',
    deviatorStressKPa: 'residualDeviatorStressKPa',
    cambridgePKPa: 'residualCambridgePKPa',
    effCambridgePKPa: 'residualEffCambridgePKPa',
    tauTKPa: 'residualTauTKPa',
    meanStressSKPa: 'residualMeanStressSKPa',
    effStressRatio: 'residualEffStressRatio',
    excessPwpKPa: 'residualExcessPwpKPa',
  },
};

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function readRangeFromGeneral(general: TriaxialGeneral, slot: TriaxialSlot): SlotRange {
  const keys = SLOT_RANGE_KEYS[slot];
  return {
    fromPct: numOrNull(general[keys.from]),
    toPct: numOrNull(general[keys.to]),
  };
}

function readSnapshotFromGeneral(general: TriaxialGeneral, slot: TriaxialSlot): SlotSnapshot {
  const keys = SLOT_SNAPSHOT_KEYS[slot];
  return {
    axialStrainPct: numOrNull(general[keys.axialStrainPct]),
    deviatorStressKPa: numOrNull(general[keys.deviatorStressKPa]),
    cambridgePKPa: numOrNull(general[keys.cambridgePKPa]),
    effCambridgePKPa: numOrNull(general[keys.effCambridgePKPa]),
    tauTKPa: numOrNull(general[keys.tauTKPa]),
    meanStressSKPa: numOrNull(general[keys.meanStressSKPa]),
    effStressRatio: numOrNull(general[keys.effStressRatio]),
    excessPwpKPa: numOrNull(general[keys.excessPwpKPa]),
  };
}

export const TriaxialAnalysis = ({
  workOrderDetailKey, general, readings, onReloadRequested,
  onSilentUpdateGeneral, onNotify, readOnly,
}: Props) => {
  const testType = general.testType ?? 'CIU';
  const cons = useMemo(() => readings.filter((r) => r.phase === 'Consolidation'), [readings]);
  const shear = useMemo(() => readings.filter((r) => r.phase === 'Shear'), [readings]);

  // ── Build chart series data ─────────────────────────────────────────
  const consolData = useMemo(() => cons.flatMap((r) => {
    if (r.computedConsolidationTimeS == null || r.computedConsAxialDisplacementMm == null) return [];
    const t = Number(r.computedConsolidationTimeS);
    const ad = Number(r.computedConsAxialDisplacementMm);
    if (!Number.isFinite(t) || !Number.isFinite(ad) || t < 0) return [];
    return [[Math.sqrt(t), ad / 10]];
  }), [cons]);

  const deviatorData = useMemo(() => shear.flatMap((r) => {
    if (r.computedAxialStrainPct == null || r.computedDeviatorStressKPa == null) return [];
    return [[Number(r.computedAxialStrainPct), Number(r.computedDeviatorStressKPa)]];
  }), [shear]);

  const stressRatioData = useMemo(() => shear.flatMap((r) => {
    if (r.computedAxialStrainPct == null || r.computedEffStressRatio == null) return [];
    return [[Number(r.computedAxialStrainPct), Number(r.computedEffStressRatio)]];
  }), [shear]);

  const pwpVolData = useMemo(() => shear.flatMap((r) => {
    if (r.computedAxialStrainPct == null) return [];
    if (testType === 'CIU') {
      if (r.computedExcessPwpKPa == null) return [];
      return [[Number(r.computedAxialStrainPct), Number(r.computedExcessPwpKPa)]];
    }
    if (r.computedVolumeChangeMm3 == null) return [];
    return [[Number(r.computedAxialStrainPct), Number(r.computedVolumeChangeMm3) / 1000]];
  }), [shear, testType]);

  const qpData = useMemo(() => shear.flatMap((r) => {
    if (r.computedEffCambridgePKPa == null || r.computedDeviatorStressKPa == null) return [];
    return [[Number(r.computedEffCambridgePKPa), Number(r.computedDeviatorStressKPa)]];
  }), [shear]);

  const tsData = useMemo(() => shear.flatMap((r) => {
    if (r.computedEffSigma1KPa == null || r.computedEffSigma3KPa == null || r.computedMaxShearStressTKPa == null) return [];
    return [[(Number(r.computedEffSigma1KPa) + Number(r.computedEffSigma3KPa)) / 2, Number(r.computedMaxShearStressTKPa)]];
  }), [shear]);

  // ── Picker state — ranges (typed inputs) ───────────────────────────
  // Derive initial picker state from the persisted general row. Snapshot
  // values live alongside ranges so the COMPUTED REPORT VALUES card reads
  // from the snapshot, not by recomputing on render.
  const [rangesLocal, setRangesLocal] = useState<Record<TriaxialSlot, SlotRange>>(() => ({
    max: readRangeFromGeneral(general, 'max'),
    peak: readRangeFromGeneral(general, 'peak'),
    end: readRangeFromGeneral(general, 'end'),
    residual: readRangeFromGeneral(general, 'residual'),
  }));
  const [saving, setSaving] = useState(false);

  // Live snapshots derived from current rangesLocal + shear readings.
  // These drive the live preview in the picker chips and the marker dots
  // on the charts. They are recomputed locally so the operator sees
  // immediate feedback without round-tripping to the server. On save,
  // these exact values are persisted via the picker payload.
  const snapshotsLive = useMemo<Record<TriaxialSlot, SlotSnapshot>>(() => ({
    max: resolveSlotSnapshot('max', rangesLocal.max, shear),
    peak: resolveSlotSnapshot('peak', rangesLocal.peak, shear),
    end: resolveSlotSnapshot('end', rangesLocal.end, shear),
    residual: resolveSlotSnapshot('residual', rangesLocal.residual, shear),
  }), [rangesLocal, shear]);

  const slotStates: SlotState[] = useMemo(
    () => TRIAXIAL_SLOT_ORDER.map((slot) => ({
      slot,
      range: rangesLocal[slot],
      snapshot: snapshotsLive[slot],
    })),
    [rangesLocal, snapshotsLive],
  );

  // Persisted snapshots — what to render on the COMPUTED REPORT VALUES
  // card. These read directly from `general.<slot>...`, NOT from
  // snapshotsLive, so the card reflects the saved value (the report PDF
  // also reads from the same snapshot, keeping screen-vs-report parity).
  const snapshotsPersisted = useMemo<Record<TriaxialSlot, SlotSnapshot>>(() => ({
    max: readSnapshotFromGeneral(general, 'max'),
    peak: readSnapshotFromGeneral(general, 'peak'),
    end: readSnapshotFromGeneral(general, 'end'),
    residual: readSnapshotFromGeneral(general, 'residual'),
  }), [general]);

  const onRangeChange = useCallback(
    (slot: TriaxialSlot, key: 'fromPct' | 'toPct', value: string) => {
      const v = value === '' ? null : Number(value);
      const next = (v !== null && Number.isFinite(v)) ? v : null;
      setRangesLocal((prev) => ({
        ...prev,
        [slot]: { ...prev[slot], [key]: next },
      }));
    },
    [],
  );

  const onSavePicker = async () => {
    setSaving(true);
    try {
      const payload = buildPickerPayload(rangesLocal, snapshotsLive);
      await updateTriaxialPicker(workOrderDetailKey, payload);
      onReloadRequested();
    } catch (err) {
      // BUG-2026-0111 — a rejected save used to vanish silently here.
      onNotify(apiErrorMessage(err) ?? 'Failed to save analysis picker values', 'error');
    } finally {
      setSaving(false);
    }
  };

  // BUG-2026-0109 — persist the operator-selected φ' report overrides.
  // Smart update (no full reload): patch only the two φ' fields in parent
  // state via onSilentUpdateGeneral instead of calling onReloadRequested()
  // (which triggers setLoading(true) → full spinner). The picker save still
  // uses onReloadRequested() because that save triggers server-side
  // recomputation of τ/φ' caches that need to be fetched fresh.
  const onSaveSelectedPhi = useCallback(
    async (phiMaxDeg: number | null, phiEndDeg: number | null) => {
      try {
        await updateTriaxialSelectedPhi(workOrderDetailKey, {
          selectedPhiMaxDeg: phiMaxDeg,
          selectedPhiEndDeg: phiEndDeg,
        });
        onSilentUpdateGeneral({ selectedPhiMaxDeg: phiMaxDeg, selectedPhiEndDeg: phiEndDeg });
        onNotify('Selected φ′ values saved', 'success');
      } catch (err) {
        onNotify(
          apiErrorMessage(err) ?? 'Failed to save selected φ′ values — they will not persist',
          'error',
        );
      }
    },
    [workOrderDetailKey, onSilentUpdateGeneral, onNotify],
  );

  // ── Charts (Direct-Shear style, with REQ-07 plotBands + slot markers) ──
  const driverPlotBands = useMemo(() => buildSlotPlotBands(slotStates), [slotStates]);
  const driverPlotLines = useMemo(() => buildPointPickPlotLines(slotStates), [slotStates]);

  const deviatorMarkers = useMemo(
    () => buildSlotMarkers(
      slotStates,
      (s) => s.axialStrainPct,
      (s) => s.deviatorStressKPa,
      (v) => `εₐ = ${fmtX(v)}`,
      (v) => `q = ${fmtKpa(v)}`,
      '%', ' kPa',
    ),
    [slotStates],
  );

  const stressRatioMarkers = useMemo(
    () => buildSlotMarkers(
      slotStates,
      (s) => s.axialStrainPct,
      (s) => s.effStressRatio,
      (v) => `εₐ = ${fmtX(v)}`,
      (v) => `σ′₁/σ′₃ = ${v.toFixed(2)}`,
      '%', '',
    ),
    [slotStates],
  );

  const pwpVolMarkers = useMemo(
    () => buildSlotMarkers(
      slotStates,
      (s) => s.axialStrainPct,
      (s) => s.excessPwpKPa,
      (v) => `εₐ = ${fmtX(v)}`,
      (v) => `Δu = ${fmtKpa(v)}`,
      '%', ' kPa',
    ),
    [slotStates],
  );

  const qpMarkers = useMemo(
    () => buildSlotMarkers(
      slotStates,
      (s) => s.effCambridgePKPa,
      (s) => s.deviatorStressKPa,
      (v) => `p′ = ${fmtKpa(v)}`,
      (v) => `q = ${fmtKpa(v)}`,
      ' kPa', ' kPa',
    ),
    [slotStates],
  );

  const tsMarkers = useMemo(
    () => buildSlotMarkers(
      slotStates,
      (s) => s.meanStressSKPa,
      (s) => s.tauTKPa,
      (v) => `s = ${fmtKpa(v)}`,
      (v) => `t = ${fmtKpa(v)}`,
      ' kPa', ' kPa',
    ),
    [slotStates],
  );

  // Build the 6 chart options. The 3 strain-x charts share plotBands +
  // plotLines (driver = deviator; mirrored = stress-ratio + pwp).
  // qp / ts get marker-only overlays.
  //
  // xSoftMax: per BUG-2026-0066, axial-strain plots floor the X-axis at 30 %
  // so the chart always shows εₐ ∈ [0, 30] even when the test ended below
  // that. Highcharts still stretches past softMax if data exceeds it.
  const buildOpts = (
    data: number[][],
    title: string,
    xTitle: string,
    yTitle: string,
    extraSeries: Highcharts.SeriesOptionsType[] = [],
    plotBands?: Highcharts.XAxisPlotBandsOptions[],
    plotLines?: Highcharts.XAxisPlotLinesOptions[],
    xSoftMax?: number,
  ): Highcharts.Options => ({
    ...baseChartOptions,
    title: { text: title, style: { fontSize: '14px', fontWeight: '600' } },
    xAxis: {
      ...(baseChartOptions.xAxis as Highcharts.XAxisOptions),
      title: { text: xTitle },
      ...(plotBands && plotBands.length ? { plotBands } : {}),
      ...(plotLines && plotLines.length ? { plotLines } : {}),
      ...(xSoftMax !== undefined ? { softMax: xSoftMax } : {}),
    },
    yAxis: {
      ...(baseChartOptions.yAxis as Highcharts.YAxisOptions),
      title: { text: yTitle },
    },
    tooltip: {
      ...(baseChartOptions.tooltip as Highcharts.TooltipOptions),
      headerFormat: '<span style="font-size:10px;color:#666">{point.key:.3f}</span><br/>',
      pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y:.3f}</b>',
    },
    series: [
      {
        type: 'spline',
        name: yTitle,
        data: data as Highcharts.PointOptionsType[],
        color: CHART_COLORS.blue,
      },
      ...extraSeries,
    ],
  });

  // BUG-2026-0066 — axial-strain plots floor the x-axis at 30 % εₐ.
  const AXIAL_STRAIN_SOFT_MAX = 30;

  const consolOpts = buildOpts(consolData, 'Consolidation Stage — Axial Displacement vs √t',
    'Square Root Time (s^0.5)', 'Axial Displacement (cm)');
  const deviatorOpts = buildOpts(
    deviatorData,
    'Shearing — Deviator Stress vs Axial Strain  (driver chart for picker)',
    'Axial Strain εₐ (%)', 'Deviator Stress q (kPa)',
    deviatorMarkers, driverPlotBands, driverPlotLines, AXIAL_STRAIN_SOFT_MAX,
  );
  const stressRatioOpts = buildOpts(
    stressRatioData,
    'Effective Stress Ratio vs Axial Strain  (mirrored bands)',
    'Axial Strain εₐ (%)', "σ'₁ / σ'₃",
    stressRatioMarkers, driverPlotBands, driverPlotLines, AXIAL_STRAIN_SOFT_MAX,
  );
  const pwpVolOpts = buildOpts(
    pwpVolData,
    testType === 'CIU'
      ? 'Excess Pore Pressure vs Axial Strain  (mirrored bands)'
      : 'Volume Change vs Axial Strain',
    'Axial Strain εₐ (%)',
    testType === 'CIU' ? 'Excess PWP Δu (kPa)' : 'Volume Change ΔV (cm³)',
    testType === 'CIU' ? pwpVolMarkers : [],
    testType === 'CIU' ? driverPlotBands : undefined,
    testType === 'CIU' ? driverPlotLines : undefined,
    AXIAL_STRAIN_SOFT_MAX,
  );
  const qpOpts = buildOpts(qpData, "Stress Path — q vs p'", "p' (kPa)", 'q (kPa)', qpMarkers);
  const tsOpts = buildOpts(tsData, "Stress Path — t vs s'", "s' (kPa)", 't (kPa)', tsMarkers);

  // ── Synced charts (crosshair + zoom across all 6) ───────────────────
  const { registerChart, syncCrosshair, hideCrosshairs, syncZoom, resetAllZoom } = useSyncedCharts();
  const chartProps = {
    onSyncCrosshair: syncCrosshair,
    onHideCrosshairs: hideCrosshairs,
    onSyncZoom: syncZoom,
    registerChart,
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* BUG-2026-0109 — the strain-range Analysis Picker confused report
          reviewers ("we don't need that"), so it is tucked into a
          collapsed-by-default Advanced section. It still works exactly as
          before for operators who need to adjust the per-slot εₐ ranges
          (its Save persists the snapshots the report values derive from). */}
      <Accordion disableGutters defaultExpanded={false} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Advanced — Analysis Picker (εₐ strain ranges)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <TriaxialPickerInputs
            rows={TRIAXIAL_SLOT_ORDER.map((slot) => ({
              slot,
              label: TRIAXIAL_SLOT_LABEL[slot],
              range: rangesLocal[slot],
              snapshot: snapshotsLive[slot],
            }))}
            saving={saving}
            readOnly={readOnly}
            onRangeChange={onRangeChange}
            onSave={onSavePicker}
          />
        </AccordionDetails>
      </Accordion>

      <TriaxialComputedValuesCard
        general={general}
        snapshots={snapshotsPersisted}
        readOnly={readOnly}
        onSaveSelectedPhi={onSaveSelectedPhi}
      />

      <TriaxialSummaryTable general={general} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
        <Button
          size="small" variant="outlined"
          startIcon={<ZoomOutMap sx={{ fontSize: 16 }} />}
          onClick={resetAllZoom}
          sx={{ textTransform: 'none', fontSize: '0.78rem' }}
        >
          Reset All Zoom
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
        <SyncedChart options={consolOpts} chartIndex={0} {...chartProps} />
        <SyncedChart options={deviatorOpts} chartIndex={1} {...chartProps} />
        <SyncedChart options={stressRatioOpts} chartIndex={2} {...chartProps} />
        <SyncedChart options={pwpVolOpts} chartIndex={3} {...chartProps} />
        <SyncedChart options={qpOpts} chartIndex={4} {...chartProps} />
        <SyncedChart options={tsOpts} chartIndex={5} {...chartProps} />
      </Box>
    </Box>
  );
};

/**
 * Build the full picker payload sent to the backend. Includes:
 *  - new range + snapshot fields (authoritative)
 *  - legacy single-strain fields populated from the resolved snapshot, so
 *    the backend report-summary cache (τ_max / τ_end / φ'_max / φ'_end /
 *    Skempton's A) keeps computing correctly until that path is migrated
 *    to read the snapshot columns directly.
 */
function buildPickerPayload(
  ranges: Record<TriaxialSlot, SlotRange>,
  snapshots: Record<TriaxialSlot, SlotSnapshot>,
): TriaxialPicker {
  const sMax = snapshots.max ?? EMPTY_SNAPSHOT;
  const sPeak = snapshots.peak ?? EMPTY_SNAPSHOT;
  const sEnd = snapshots.end ?? EMPTY_SNAPSHOT;
  const sRes = snapshots.residual ?? EMPTY_SNAPSHOT;

  return {
    // Legacy single-strain (mirror the snapshot)
    maxPointStrainPct: sMax.axialStrainPct,
    maxPointDeviatorKPa: sMax.deviatorStressKPa,
    maxPointPwpKPa: sMax.excessPwpKPa,
    peakPointStrainPct: sPeak.axialStrainPct,
    peakPointDeviatorKPa: sPeak.deviatorStressKPa,
    endPointStrainPct: sEnd.axialStrainPct,
    endPointDeviatorKPa: sEnd.deviatorStressKPa,
    endPointPwpKPa: sEnd.excessPwpKPa,
    residualPointStrainPct: sRes.axialStrainPct,
    residualPointDeviatorKPa: sRes.deviatorStressKPa,

    // REQ-2026-0007 — Max slot range + snapshot
    maxFromPct: ranges.max.fromPct,
    maxToPct: ranges.max.toPct,
    maxAxialStrainPct: sMax.axialStrainPct,
    maxDeviatorStressKPa: sMax.deviatorStressKPa,
    maxCambridgePKPa: sMax.cambridgePKPa,
    maxEffCambridgePKPa: sMax.effCambridgePKPa,
    maxTauTKPa: sMax.tauTKPa,
    maxMeanStressSKPa: sMax.meanStressSKPa,
    maxEffStressRatio: sMax.effStressRatio,
    maxExcessPwpKPa: sMax.excessPwpKPa,

    // Peak slot range + snapshot
    peakFromPct: ranges.peak.fromPct,
    peakToPct: ranges.peak.toPct,
    peakAxialStrainPct: sPeak.axialStrainPct,
    peakDeviatorStressKPa: sPeak.deviatorStressKPa,
    peakCambridgePKPa: sPeak.cambridgePKPa,
    peakEffCambridgePKPa: sPeak.effCambridgePKPa,
    peakTauTKPa: sPeak.tauTKPa,
    peakMeanStressSKPa: sPeak.meanStressSKPa,
    peakEffStressRatio: sPeak.effStressRatio,
    peakExcessPwpKPa: sPeak.excessPwpKPa,

    // End slot range + snapshot
    endFromPct: ranges.end.fromPct,
    endToPct: ranges.end.toPct,
    endAxialStrainPct: sEnd.axialStrainPct,
    endDeviatorStressKPa: sEnd.deviatorStressKPa,
    endCambridgePKPa: sEnd.cambridgePKPa,
    endEffCambridgePKPa: sEnd.effCambridgePKPa,
    endTauTKPa: sEnd.tauTKPa,
    endMeanStressSKPa: sEnd.meanStressSKPa,
    endEffStressRatio: sEnd.effStressRatio,
    endExcessPwpKPa: sEnd.excessPwpKPa,

    // Residual slot range + snapshot
    residualFromPct: ranges.residual.fromPct,
    residualToPct: ranges.residual.toPct,
    residualAxialStrainPct: sRes.axialStrainPct,
    residualDeviatorStressKPa: sRes.deviatorStressKPa,
    residualCambridgePKPa: sRes.cambridgePKPa,
    residualEffCambridgePKPa: sRes.effCambridgePKPa,
    residualTauTKPa: sRes.tauTKPa,
    residualMeanStressSKPa: sRes.meanStressSKPa,
    residualEffStressRatio: sRes.effStressRatio,
    residualExcessPwpKPa: sRes.excessPwpKPa,
  };
}
